import { z } from "zod";
import type {
    OrganizationDetail,
    UpdateOrganizationRequest,
} from "@models/organization";
import { prefillUsPhone } from "@customer/Marketing/_helpers/formatUsPhone";
import {
    addressFormFields,
    canonicalState,
    countryToSend,
    requiredText as required,
} from "./addressForm";

// The organization form (C5), shared by the Edit Organization drawer and, per
// the C3/C4 Lead call, the New Client drawer (createClientForm). Its address
// rules are the Edit Location drawer's too (addressForm).

// Mirrors DomainConstraint on the API, so a length UpdateOrganizationValidator
// would refuse with a 400 is caught inline. The phone rule below already
// bounds that field well inside its limit.
const MAX = {
    name: 256,
    legalBusinessName: 256,
    firstName: 128,
    lastName: 128,
    email: 256,
} as const;

export const organizationFormSchema = z.object({
    name: required("Org name", MAX.name),
    legalBusinessName: required("Legal business name", MAX.legalBusinessName),
    ...addressFormFields,
    firstName: required("First name", MAX.firstName),
    lastName: required("Last name", MAX.lastName),
    email: required("Email", MAX.email).email("Enter a valid email address"),
    // The mask the apply and profile forms produce and UsPhoneNumber enforces.
    phoneNumber: z
        .string()
        .trim()
        .nonempty("Phone is required")
        .regex(/^\(\d{3}\) \d{3}-\d{4}$/, "Enter a valid 10-digit phone number"),
});

export type OrganizationFormValues = z.infer<typeof organizationFormSchema>;

// The phone is prefilled by the profile's rule (prefillUsPhone): exactly 10
// digits is masked, anything else shown as stored for the admin to retype. A
// client without a legal name (the API's is nullable) opens on an empty field,
// which the form's own rule then asks to be filled.
export const toOrganizationFormValues = (
    organization: OrganizationDetail,
): OrganizationFormValues => ({
    name: organization.name,
    legalBusinessName: organization.legalBusinessName ?? "",
    streetAddress: organization.streetAddress,
    city: organization.city,
    state: canonicalState(organization.state),
    zipCode: organization.zipCode,
    firstName: organization.firstName,
    lastName: organization.lastName,
    email: organization.email,
    phoneNumber: prefillUsPhone(organization.phoneNumber),
});

// `readAs` is the detail the form was filled from. Its country is sent back as
// read (not an editable field) unless blank (countryToSend), and its updatedAt
// verbatim: the API compares it to the stored instant at 100 ns, so it never
// goes through a JS Date.
export const toUpdateOrganizationRequest = (
    values: OrganizationFormValues,
    readAs: Pick<OrganizationDetail, "country" | "updatedAt">,
): UpdateOrganizationRequest => ({
    name: values.name,
    legalBusinessName: values.legalBusinessName,
    streetAddress: values.streetAddress,
    city: values.city,
    state: values.state,
    zipCode: values.zipCode,
    country: countryToSend(readAs.country),
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phoneNumber: values.phoneNumber,
    updatedAt: readAs.updatedAt,
});
